/**
 * Shared utility functions for benefit plan display logic
 * Used by RatesGrid and SummaryGrid components
 */

import type { BenefitPlan } from "../types/models";
import type { MenuItemDef } from "ag-grid-community";

const NOT_APPLICABLE = "notApplicable";
const EDIT_RENEWAL_RATES_MENU_ITEM = "Edit Renewal Rates";
const EDIT_RATE_STRUCTURE_MENU_ITEM = "Edit Rate Structure";

/**
 * Capitalize the first letter of a string (e.g. "low" → "Low")
 */
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Check if a plan has the "alternate" policyPeriod
 * Alternate plans are newly created plan groups that don't have rates yet
 */
export const isAlternatePlan = (plan: BenefitPlan): boolean =>
  plan.policyPeriod === "alternate";

/**
 * Determine if a plan should show rates content
 * Plans with "alternate" policyPeriod don't have rates
 */
export const shouldShowRatesContent = (plan: BenefitPlan): boolean =>
  !isAlternatePlan(plan);

/**
 * Determine if header menu should be suppressed for a plan
 * Alternate plans have no rates to edit, so menu should be hidden
 */
export const shouldSuppressHeaderMenu = (plan: BenefitPlan): boolean =>
  isAlternatePlan(plan);

/**
 * Get the display name for a benefit plan.
 * - Alternate plans return empty string (no rates to show).
 * - When option name is meaningful (not "notApplicable"), shows "OptionName - Identifier"
 *   e.g. "Low - DEN-1-1".
 * - When option name is "notApplicable" or missing, shows just the identifier
 *   e.g. "DEN-1-1".
 */
export const getPlanHeaderName = (plan: BenefitPlan): string => {
  if (isAlternatePlan(plan)) {
    return "";
  }

  if (plan.optionName && plan.optionName !== NOT_APPLICABLE) {
    return `${capitalize(plan.optionName)} - ${plan.identifier}`;
  }

  return plan.identifier || "Plan";
};

export interface RateEditMenuItemOptions {
  /** True for the rate-sets (Incumbent/Current) column — names the item "Edit Renewal Rates". */
  isRateSetsPlan: boolean;
  /**
   * True when the plan has no backing proposal_plan row yet (pad/placeholder
   * plans on the Quotes SBS grid). Renders the item disabled with `tooltip`
   * instead of wiring `action`.
   */
  isDisabled: boolean;
  /** Shown as the item's tooltip when `isDisabled` is true. */
  tooltip?: string;
  /** Opens the RatesDrawer for this plan. Omitted from the item when `isDisabled` is true. */
  action: () => void;
}

/**
 * Builds the "Edit Rate Structure"/"Edit Renewal Rates" header-menu item for a
 * single plan column, disabled with a tooltip when the plan can't have rates
 * yet.
 */
export const buildRateEditMenuItem = ({
  isRateSetsPlan,
  isDisabled,
  tooltip,
  action,
}: RateEditMenuItemOptions): MenuItemDef => {
  const name = isRateSetsPlan
    ? EDIT_RENEWAL_RATES_MENU_ITEM
    : EDIT_RATE_STRUCTURE_MENU_ITEM;

  if (isDisabled) {
    return { name, disabled: true, tooltip };
  }

  return { name, action };
};
